import { ChangeEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { notify } from '../../toaster';
import { useDatasetUpload } from '../hooks/useDatasetUpload';
import { UploadArea } from './UploadArea';

const maxFileSize = 52428800; // 50 MB

export const ReplaceDataDialog = ({
  open,
  onClose,
  datasetName,
  hasRows,
}: {
  open: boolean;
  onClose: () => void;
  datasetName: string;
  // An empty dataset gets its first data instead of having it replaced.
  hasRows: boolean;
}) => {
  const intl = useIntl();
  const [file, setFile] = useState<File | null>(null);
  const { mutate: uploadData, isLoading: isReplacing } = useDatasetUpload();

  // The dialog stays mounted, so a closed one must not keep the last file.
  const handleClose = () => {
    setFile(null);
    onClose();
  };

  const actionLabel = intl.formatMessage({
    id: hasRows
      ? 'features.datasets.data.replace'
      : 'features.datasets.data.upload',
  });

  return (
    <Dialog
      open={open}
      component="form"
      onSubmit={e => {
        e.preventDefault();
        if (file) {
          uploadData(
            {
              file,
              datasetName,
              encoding: 'utf-8',
              dynamic: true,
            },
            {
              onSuccess: () => {
                notify.default({
                  title: intl.formatMessage({
                    id: 'features.datasets.notification.importStarted',
                  }),
                });
                handleClose();
              },
              onError: () => {
                notify.error({
                  title: intl.formatMessage({
                    id: 'features.datasets.notification.importFailed',
                  }),
                });
              },
            }
          );
        }
      }}
      onClose={handleClose}
    >
      <DialogTitle title={actionLabel} onClose={handleClose} />
      <DialogContent>
        <Stack pt={2} spacing={2}>
          <Stack spacing={1}>
            <Typography variant="body1">
              {intl.formatMessage({
                id: hasRows
                  ? 'features.datasets.data.replace.description'
                  : 'features.datasets.data.upload.description',
              })}
            </Typography>
            {hasRows ? (
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: 'features.datasets.data.replace.descriptionWarning',
                })}
              </Typography>
            ) : null}
          </Stack>
          <UploadArea
            file={file}
            onUploadChange={(e: ChangeEvent<HTMLInputElement>) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) {
                if (selectedFile.size > maxFileSize) {
                  // return setFileSizeError(true);
                }
                // setFileSizeError(false);
                return setFile(selectedFile);
              }
              return setFile(null);
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!file || isReplacing}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
